import React, { useEffect, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Badge } from "./ui/badge";
import fetchWithAuth from "../fetchWithAuth";

function getStatusClasses(status) {
  switch (status) {
    case "done":
      return "bg-green-50 text-green-700 border-green-200";
    case "error":
      return "bg-red-50 text-red-700 border-red-200";
    case "sent":
      return "bg-blue-50 text-blue-700 border-blue-200";
    default:
      return "bg-yellow-100 text-yellow-900 border-yellow-300";
  }
}

const scheduleDateFormatter = new Intl.DateTimeFormat("ru-RU", {
  timeZone: "Europe/Moscow", year: "numeric", month: "2-digit", day: "2-digit",
  hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
});

function formatDate(dateString) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (!Number.isFinite(date.getTime())) return "-";
  const parts = Object.fromEntries(scheduleDateFormatter.formatToParts(date).map(({ type, value }) => [type, value]));
  return `${parts.hour}:${parts.minute}:${parts.second} ${parts.day}.${parts.month}.${parts.year}`;
}

export default function ScheduledTasksTab({ projectId }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!projectId) return;

    setLoading(true);
    setError("");

    fetchWithAuth(`/api/project/${projectId}/scheduled-tasks`)
      .then(async (res) => {
        if (!res.ok) {
          throw new Error("Ошибка загрузки расписания");
        }
        return res.json();
      })
      .then((data) => {
        setTasks(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => {
        setError("Ошибка загрузки расписания");
        setTasks([]);
        setLoading(false);
      });
  }, [projectId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-600">
        Заданий в расписании: <span className="font-semibold">{tasks.length}</span>
        <p className="mt-1 text-xs text-gray-400">С начала сегодняшних суток по МСК, включая задания на следующие дни.</p>
      </div>

      <div className="overflow-x-auto">
        <Table className="min-w-[880px] table-fixed [&_th]:px-3 [&_td]:px-3 [&_td]:py-3 [&_td]:align-top">
          <colgroup>
            <col className="w-[4%]" />
            <col className="w-[26%]" />
            <col className="w-[23%]" />
            <col className="w-[12%]" />
            <col className="w-[8%]" />
            <col className="w-[17%]" />
            <col className="w-[10%]" />
          </colgroup>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead>
              <TableHead>Домен клика</TableHead>
              <TableHead>Запрос</TableHead>
              <TableHead>Доп. слово</TableHead>
              <TableHead className="text-center">Регион</TableHead>
              <TableHead>Запланированное время (МСК)</TableHead>
              <TableHead className="text-center">Статус</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tasks.map((task) => (
              <TableRow key={task.id}>
                <TableCell>{task.index}</TableCell>
                <TableCell className="whitespace-normal leading-relaxed [overflow-wrap:anywhere]">{task.clickDomain || "-"}</TableCell>
                <TableCell className="whitespace-normal [overflow-wrap:anywhere]">{task.query || "-"}</TableCell>
                <TableCell className="whitespace-normal [overflow-wrap:anywhere]">{task.extraWord || "-"}</TableCell>
                <TableCell className="text-center">{task.region || "-"}</TableCell>
                <TableCell className="tabular-nums">{formatDate(task.scheduled_time)}</TableCell>
                <TableCell className="text-center">
                  <Badge variant="outline" className={getStatusClasses(task.displayStatus)}>
                    {task.displayStatusLabel}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}

            {tasks.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-6 text-gray-500">
                  На сегодня запланированных заданий нет
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
